import {Sidebar} from '@github-ui/layout-primitives/Sidebar'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ToolsIcon} from '@primer/octicons-react'
import {useMemo} from 'react'
import {SidebarNavItem, type SidebarAnalyticsKey} from './SidebarNavItem'
import type {WorkflowSidebarSelection} from './WorkflowSidebar'

interface ManagementItem {
  analyticsKey: SidebarAnalyticsKey
  label: string
  href: string
  selection?: WorkflowSidebarSelection['type']
  softNavigationEnabled?: boolean
  external?: boolean
}

export interface WorkflowSidebarManagementProps {
  owner: string
  repo: string
  softNavigationEnabled: boolean
  showDeployments: boolean
  showAttestations: boolean
  showRunners: boolean
  lockfilePath?: string | null
  showUsageMetrics: boolean
  selection: WorkflowSidebarSelection
}

export function WorkflowSidebarManagement({
  owner,
  repo,
  softNavigationEnabled,
  showDeployments,
  showAttestations,
  showRunners,
  lockfilePath,
  showUsageMetrics,
  selection,
}: WorkflowSidebarManagementProps) {
  const managementItems = useMemo<ManagementItem[]>(() => {
    const nativeCachesEnabled = isFeatureEnabled('actions_caches_react_view')
    const items: ManagementItem[] = [
      {
        analyticsKey: 'caches',
        label: 'Caches',
        href: `/${owner}/${repo}/actions/caches`,
        selection: 'caches',
        softNavigationEnabled: softNavigationEnabled && nativeCachesEnabled,
      },
    ]
    if (showDeployments) {
      items.push({
        analyticsKey: 'deployments',
        label: 'Deployments',
        href: `/${owner}/${repo}/deployments`,
        external: true,
      })
    }
    if (showAttestations) {
      items.push({
        analyticsKey: 'attestations',
        label: 'Attestations',
        href: `/${owner}/${repo}/attestations`,
        external: true,
      })
    }
    if (showRunners) {
      const nativeRunnersEnabled = isFeatureEnabled('actions_runners_react_view')
      items.push({
        analyticsKey: 'runners',
        label: 'Runners',
        href: `/${owner}/${repo}/actions/runners`,
        selection: 'runners',
        softNavigationEnabled: softNavigationEnabled && nativeRunnersEnabled,
      })
    }
    if (lockfilePath) {
      items.push({analyticsKey: 'lockfile', label: 'Lockfile', href: lockfilePath, external: true})
    }
    if (showUsageMetrics) {
      items.push(
        {
          analyticsKey: 'usage',
          label: 'Usage metrics',
          href: `/${owner}/${repo}/actions/metrics/usage`,
          external: true,
        },
        {
          analyticsKey: 'performance',
          label: 'Performance metrics',
          href: `/${owner}/${repo}/actions/metrics/performance`,
          external: true,
        },
      )
    }
    return items
  }, [
    lockfilePath,
    owner,
    repo,
    showAttestations,
    showDeployments,
    showRunners,
    showUsageMetrics,
    softNavigationEnabled,
  ])

  return (
    <Sidebar aria-label="Management">
      <Sidebar.Section
        title="Management"
        variant="collapsible"
        icon={ToolsIcon}
        headingLevel="h2"
        divider
        current={managementItems.some(item => item.selection === selection.type)}
      >
        {managementItems.map(item => (
          <SidebarNavItem
            analyticsKey={item.analyticsKey}
            key={item.label}
            label={item.label}
            href={item.href}
            isCurrent={item.selection === selection.type}
            softNavigationEnabled={item.softNavigationEnabled}
            data-turbo={item.selection === 'caches' || item.selection === 'runners' ? 'false' : undefined}
            external={item.external}
          />
        ))}
      </Sidebar.Section>
    </Sidebar>
  )
}

WorkflowSidebarManagement.displayName = 'WorkflowSidebarManagement'
